import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Palette, Check, Paintbrush } from 'lucide-react';
import { Button } from './ui/button';
import { THEMES, themeVarsFromPrefs, applyTheme, getSavedTheme } from '../utils/theme';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = `${getBackendUrl()}/api`;

export const ThemeSettings = () => {
  const [prefs, setPrefs] = useState(() => getSavedTheme() || { preset: 'classic', accent: null });
  const [customColor, setCustomColor] = useState(getSavedTheme()?.accent || '#7c3aed');

  useEffect(() => {
    axios.get(`${API}/user/theme-prefs`).then((r) => {
      if (r.data?.preset) {
        setPrefs(r.data);
        if (r.data.accent) setCustomColor(r.data.accent);
        applyTheme(r.data);
      }
    }).catch(() => {});
  }, []);

  const save = async (next) => {
    setPrefs(next);
    applyTheme(next);
    try {
      await axios.put(`${API}/user/theme-prefs`, next);
      toast.success('Theme saved — it will follow you on any device');
    } catch {
      toast.error('Could not save theme');
    }
  };

  return (
    <div className="glass-card p-6 mb-6" data-testid="theme-settings">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-3 bg-purple-500/20 rounded-lg">
          <Palette className="w-6 h-6 text-purple-500" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Appearance</h2>
          <p className="text-sm text-gray-500">Pick a look you like — it only changes the app for you</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        {Object.entries(THEMES).map(([key, t]) => {
          const selected = prefs.preset === key;
          return (
            <button
              key={key}
              onClick={() => save({ preset: key, accent: null })}
              className={`rounded-xl overflow-hidden border-2 text-left transition-transform hover:scale-[1.03] ${selected ? 'border-blue-600 shadow-lg' : 'border-gray-200'}`}
              data-testid={`theme-${key}`}
            >
              <div className="h-8" style={{ background: t.navBg }} />
              <div className="h-10" style={{ background: t.appBg }} />
              <div className="px-2.5 py-2 bg-white flex items-center justify-between">
                <span className="text-xs font-bold text-gray-800">{t.label}</span>
                {selected && <Check className="w-4 h-4 text-blue-600" />}
              </div>
            </button>
          );
        })}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <p className="text-sm font-bold text-gray-800 flex items-center gap-2 mb-3">
          <Paintbrush className="w-4 h-4 text-purple-500" />
          Custom color
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="color"
            value={customColor}
            onChange={(e) => setCustomColor(e.target.value)}
            className="w-14 h-11 rounded-lg border border-gray-300 cursor-pointer bg-white p-1"
            data-testid="custom-color-input"
          />
          <div
            className="h-11 flex-1 min-w-[120px] rounded-lg border border-gray-200"
            style={{ background: themeVarsFromPrefs({ preset: 'custom', accent: customColor }).appBg }}
          />
          <Button
            onClick={() => save({ preset: 'custom', accent: customColor })}
            className="text-white min-h-[44px]"
            style={{ background: customColor }}
            data-testid="apply-custom-theme-btn"
          >
            {prefs.preset === 'custom' && prefs.accent === customColor ? <Check className="w-4 h-4 mr-2" /> : null}
            Use This Color
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-2">Sets the header, bottom bar and page tint to your color.</p>
      </div>
    </div>
  );
};
